import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import "../Landing.css";
import { TrustStrip } from "./_shared";

/**
 * Modèle 2 v2 — Palmarès ROI + confort d&apos;été + déperditions.
 * Reproduit le mockup 11-palmares-roi-confort-ete.html avec toutes les
 * corrections juridiques (L.121-2, L.271-6 CCH, Directive 2024/825) et
 * SEO (tables sémantiques, SVG accessible, mots-clés, JSON-LD via index.html).
 */

const GESTES: { name: string; years: string; saving: string; pct: number; color: string; cls: string; danger?: boolean }[] = [
  { name: "Isolation des combles", years: "≈ 4 ans", saving: "≈ −210 €/an", pct: 27, color: "#34c759", cls: "fast" },
  { name: "Calorifugeage des tuyaux", years: "≈ 5 ans", saving: "≈ −90 €/an", pct: 33, color: "#86d94a", cls: "fast" },
  { name: "VMC hygroréglable", years: "≈ 8 ans", saving: "≈ −130 €/an", pct: 53, color: "#ffd60a", cls: "mid" },
  { name: "PAC air/eau ⚠️", years: "≈ 23 ans", saving: "≈ −620 €/an", pct: 95, color: "#ff3b30", cls: "slow", danger: true },
];

const POSTES = [
  { rank: "1", poste: "Toiture / combles", pct: "≈ 31 %", roi: "Rentable en ~4 ans", good: true },
  { rank: "2", poste: "Murs", pct: "≈ 18 %", roi: "Rentable en ~9 ans", good: false },
  { rank: "3", poste: "Ventilation", pct: "≈ 18 %", roi: "Rentable en ~8 ans", good: false },
  { rank: "4", poste: "Fenêtres", pct: "≈ 12 %", roi: "Rentable en ~12 ans", good: false },
  { rank: "5", poste: "Plancher bas", pct: "≈ 11 %", roi: "Rentable en ~15 ans", good: false },
  { rank: "6", poste: "Ponts thermiques", pct: "≈ 10 %", roi: "Inclus ci-dessus", good: false },
] as const;

export default function HomePalmares() {
  return (
    <Layout>
      <Seo
        title="Simulation DPE : par quoi commencer pour réduire votre facture"
        description="Classez les gestes de rénovation par retour sur investissement. Simulez le DPE de votre logement (méthode 3CL-DPE 2021), les déperditions et le confort d'été. Sans valeur légale."
        path="/"
      />
      <div className="home">
        {/* HERO — palmarès ROI */}
        <section className="home-wrap home-hero">
          <div className="home-glass home-hero-grid">
            <div>
              <span className="home-eyebrow">Par quoi commencer ?</span>
              <h1 className="home-h1">
                <span className="home-accent">Commencez</span> par ce qui vous rapporte le plus vite.
              </h1>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--ink-2)", marginTop: 10 }}>
                Estimation énergétique gratuite et diagnostic des gestes d&apos;isolation par rentabilité, pour préparer votre DPE officiel.
              </h2>
              <p className="home-sub">
                Mon Diag Énergétique établit le diagnostic énergétique de votre logement et classe
                chaque geste par rentabilité : le plus vite remboursé arrive en tête. Vous voyez par où
                commencer, et l&apos;ordre de grandeur des économies à la clé.
              </p>
              <div className="home-cta-row">
                <Link to="/register" className="home-btn home-btn-primary">Commencer</Link>
                <Link to="/login" className="home-btn home-btn-ghost">Se connecter</Link>
              </div>
            </div>

            {/* ROI table */}
            <div className="home-glass home-roi">
              <table className="home-roi-table">
                <caption className="home-reglette-cap">Pour une maison type de 90 m² classée F :</caption>
                <thead className="sr-only">
                  <tr>
                    <th scope="col">Geste</th>
                    <th scope="col">Retour sur investissement</th>
                    <th scope="col">Économie</th>
                  </tr>
                </thead>
                <tbody>
                  {GESTES.map((g) => (
                    <tr
                      key={g.name}
                      className="home-roi-row"
                      style={{ "--bar-color": g.color, "--bar-pct": `${g.pct}%` } as CSSProperties}
                    >
                      <th scope="row" className="name">{g.name}</th>
                      <td className={`years ${g.cls}`} style={g.danger ? { color: "#b3261e" } : undefined}>{g.years}</td>
                      <td className="saving">{g.saving}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="home-roi-note">
                Estimation indicative, aides déduites. « Rentable en X ans » = délai au
                bout duquel les économies couvrent la dépense.
                <span style={{ color: "#b3261e" }}> ⚠️ Sur cet exemple, la PAC s&apos;amortit au-delà de sa durée de vie : geste potentiellement prématuré, à réserver à un parcours plus poussé.</span>
              </p>
              <p style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>Estimation indicative, exemple 90 m² F. Sans valeur légale.</p>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* SECTION A — priorités + bouquets */}
        <section className="home-wrap home-section">
          <div className="home-section-head">
            <span className="home-eyebrow">Par quoi commencer ?</span>
            <h2>Ne gaspillez pas un euro dans le mauvais geste.</h2>
            <p className="hint">
              Chaque logement est unique. Mon Diag Énergétique estime le retour sur investissement
              de chaque geste de rénovation énergétique <strong>pour le vôtre</strong>, et vous donne l&apos;ordre
              optimal pour maximiser vos économies le plus vite possible.
            </p>
          </div>

          <div className="home-priority-grid">
            <div className="home-glass home-priority-main">
              <span className="home-eyebrow" style={{ fontSize: 11 }}>Exemple, priorité n°1 pour ce logement</span>
              <h3>Isolation des combles</h3>
              <div className="roi-badge">
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none"><circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={1.8} /><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" /></svg>
                Rentable en ~4 ans
              </div>
              <p>
                <strong>Exemple</strong> : sur cette maison type, c&apos;est le geste le plus
                vite remboursé. La toiture est son premier poste de fuite (≈ 31 % des
                déperditions). Ce classement dépend de chaque logement : Mon Diag Énergétique
                refait ce calcul <strong>pour le vôtre</strong>.
              </p>
              <p style={{ marginTop: 12, fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
                À l&apos;inverse, une pompe à chaleur air/eau serait ici
                <strong style={{ color: "#b07d3a" }}> rentable en 23 ans</strong>, au-delà de
                sa durée de vie. Risque de casse avant amortissement : pas une priorité.
              </p>
              <div className="roi-verdict">
                💡 Sur cet exemple, chaque année d&apos;attente coûte ≈ 210 €. Pour votre
                logement, le geste prioritaire peut être différent.
              </div>
            </div>
            <div className="home-glass home-priority-detail">
              <div className="label">Feuille de route (exemple)</div>
              <div className="big-eur">≈ −890 € <small>/ an</small></div>
              <div className="home-priority-bar">
                <span style={{ background: "#34c759", flex: 4 }} />
                <span style={{ background: "#86d94a", flex: 5 }} />
                <span style={{ background: "#ffd60a", flex: 8 }} />
                <span style={{ background: "#ff6a2c", flex: 11 }} />
              </div>
              <div className="home-priority-meta">
                <div className="row"><span className="k">Investissement total</span><span className="v">≈ 17 800 €</span></div>
                <div className="row"><span className="k">Aides estimées</span><span className="v">≈ −8 000 €</span></div>
                <div className="row"><span className="k">Reste à charge</span><span className="v">≈ 9 800 €</span></div>
                <div className="row"><span className="k">Économie annuelle</span><span className="v" style={{ color: "#1f7a3f" }}>≈ −890 €</span></div>
                <div className="row"><span className="k">Retour sur investissement</span><span className="v" style={{ color: "#1f7a3f" }}>≈ 9 ans</span></div>
              </div>
            </div>
          </div>

          {/* Bouquets */}
          <div className="home-section-head" style={{ marginTop: 44 }}>
            <span className="home-eyebrow">Trois parcours</span>
            <h2>Quel niveau de rénovation ?</h2>
          </div>
          <p className="hint" style={{ marginBottom: 16 }}>Exemple pour une maison de 90 m² classée F. Vos chiffres dépendent de votre logement. Aides estimées selon barèmes MaPrimeRénov&apos; et CEE 2026, sous conditions d&apos;éligibilité.</p>
          <div className="home-bouquets">
            <div className="home-glass home-bouquet">
              <div className="bq-name">Essentiel</div>
              <div className="bq-path">F → D</div>
              <div className="bq-gain">≈ −480 €/an</div>
              <div className="bq-reste">Reste ~3 200 €</div>
              <div className="bq-roi">Rentable en ~6 ans</div>
            </div>
            <div className="home-glass home-bouquet star">
              <div className="bq-name">Confort</div>
              <div className="bq-path">F → C</div>
              <div className="bq-gain">≈ −890 €/an</div>
              <div className="bq-reste">Reste ~9 500 €</div>
              <div className="bq-roi">Rentable en ~9 ans</div>
            </div>
            <div className="home-glass home-bouquet">
              <div className="bq-name">Ampleur</div>
              <div className="bq-path">F → B</div>
              <div className="bq-gain">≈ −1 250 €/an</div>
              <div className="bq-reste">Reste ~18 000 €</div>
              <div className="bq-roi">Rentable en ~13 ans</div>
            </div>
          </div>
          <div className="home-cta-row" style={{ marginTop: 24, justifyContent: "center" }}>
            <Link to="/register" className="home-btn home-btn-primary" style={{ padding: "15px 30px", fontSize: 15 }}>Comparer pour mon logement</Link>
          </div>
        </section>

        {/* SECTION B — confort hiver + été */}
        <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
          <div className="home-section-head">
            <span className="home-eyebrow">Confort thermique</span>
            <h2>L&apos;hiver, <span className="home-accent">et l&apos;été</span>.</h2>
            <p className="hint">
              Un vrai DPE ne regarde pas que le chauffage. Avec des canicules de plus en plus
              fréquentes et intenses, le confort d&apos;été devient aussi crucial que celui d&apos;hiver.
              Mon Diag Énergétique évalue les deux.
            </p>
          </div>

          <div className="home-comfort-grid">
            {/* Winter */}
            <div className="home-glass home-comfort-card">
              <div className="season-icon">❄️</div>
              <h3>Confort d&apos;hiver</h3>
              <p>
                Nous évaluons la capacité d&apos;un logement à <strong>retenir la chaleur</strong>.
                Isolation, étanchéité à l&apos;air, performance du système de chauffage : chaque
                paramètre est pris en compte.
              </p>
              <div className="indicator">
                <div className="val cold">≈ 1 450 €</div>
                <div className="ctx">Facture chauffage estimée / an</div>
              </div>
              <div className="comfort-bar">
                <div className="fill" style={{ width: "48%", background: "linear-gradient(90deg,#3b82f6,#60a5fa)" }} />
              </div>
              <div className="comfort-tags">
                <span className="home-comfort-tag blue">Déperditions : ≈ 195 kWh/m².an</span>
                <span className="home-comfort-tag blue">Classe D estimée</span>
              </div>
            </div>

            {/* Summer */}
            <div className="home-glass home-comfort-card">
              <div className="season-icon">☀️</div>
              <h3>Confort d&apos;été</h3>
              <p>
                Nous évaluons la <strong>vulnérabilité à la surchauffe</strong> estivale :
                inertie des murs, exposition, protections solaires, ventilation nocturne.
                Anticipez les canicules avant qu&apos;elles ne pèsent sur vos étés.
              </p>
              <div className="indicator">
                <div className="val hot">≈ 4 °C</div>
                <div className="ctx">Surchauffe estimée en canicule</div>
              </div>
              <div className="comfort-bar">
                <div className="fill" style={{ width: "62%", background: "linear-gradient(90deg,#ff9f0a,#ff6a2c)" }} />
              </div>
              <div className="comfort-tags">
                <span className="home-comfort-tag orange">Inertie faible</span>
                <span className="home-comfort-tag orange">Exposition sud sans protection</span>
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div className="home-glass home-comfort-verdict">
            <p>
              🌡️ Sur cet exemple, le logement perdrait ≈ 48 % de sa chaleur en hiver et subirait
              ≈ +4 °C de surchauffe en été. Isolation et protections solaires peuvent résoudre
              les deux en un seul chantier.
            </p>
            <p className="sub">
              L&apos;isolation ne sert pas qu&apos;à garder la chaleur : elle protège aussi
              de la chaleur extérieure. Un double bénéfice, un seul investissement.
            </p>
          </div>
        </section>

        {/* SECTION C — déperditions : illustration pro */}
        <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
          <div className="home-section-head">
            <span className="home-eyebrow">Diagnostic thermique</span>
            <h2>D&apos;abord savoir où part et d&apos;où vient la chaleur.</h2>
            <p className="hint">
              Avant de choisir un geste, Mon Diag Énergétique cartographie chaque poste de
              déperdition en hiver <strong>et</strong> chaque source de chaleur entrante
              en été. Vous traitez les deux en une seule analyse.
            </p>
          </div>

          <div className="home-heat-section">
            {/* SVG illustration */}
            <div className="home-glass home-heat-illustration">
              <svg viewBox="0 0 520 380" role="img" aria-labelledby="heatSvgTitle heatSvgDesc">
                <title id="heatSvgTitle">Répartition des déperditions thermiques d&apos;une maison type</title>
                <desc id="heatSvgDesc">Toiture ≈ 31 %, murs ≈ 18 %, ventilation ≈ 18 %, fenêtres ≈ 12 %, plancher ≈ 11 %, ponts thermiques ≈ 10 %.</desc>
                <defs>
                  <linearGradient id="skyGrad" x1={0} y1={0} x2={0} y2={1}>
                    <stop offset="0%" stopColor="#c8ddf0" />
                    <stop offset="100%" stopColor="#eef3f8" />
                  </linearGradient>
                  <linearGradient id="wallGrad" x1={0} y1={0} x2={1} y2={0}>
                    <stop offset="0%" stopColor="rgba(200,180,150,.35)" />
                    <stop offset="100%" stopColor="rgba(220,200,170,.35)" />
                  </linearGradient>
                </defs>
                <rect width={520} height={380} rx={16} fill="url(#skyGrad)" opacity={0.4} />
                {/* Sun */}
                <circle cx={440} cy={48} r={32} fill="#ffd60a" opacity={0.35} />
                <circle cx={440} cy={48} r={26} fill="#ffb43a" opacity={0.5} />
                <circle cx={440} cy={48} r={18} fill="#ff9f0a" opacity={0.6} />
                {/* Sun rays */}
                <line x1={440} y1={48} x2={440} y2={8} stroke="#ffd60a" strokeWidth={2} opacity={0.35} />
                <line x1={440} y1={48} x2={475} y2={18} stroke="#ffd60a" strokeWidth={2} opacity={0.3} />
                <line x1={440} y1={48} x2={405} y2={18} stroke="#ffd60a" strokeWidth={2} opacity={0.3} />
                <line x1={440} y1={48} x2={480} y2={48} stroke="#ffd60a" strokeWidth={2} opacity={0.25} />
                <line x1={440} y1={48} x2={400} y2={48} stroke="#ffd60a" strokeWidth={2} opacity={0.25} />
                {/* House roof */}
                <polygon points="100,200 250,120 400,200" fill="rgba(176,60,30,.25)" stroke="#b07d3a" strokeWidth={2.5} />
                <polygon points="100,200 250,120 400,200" fill="none" stroke="#8a5b00" strokeWidth={1} opacity={0.3} />
                {/* House body */}
                <rect x={110} y={200} width={280} height={140} fill="url(#wallGrad)" stroke="#7a8896" strokeWidth={2.5} rx={2} />
                {/* Windows left */}
                <rect x={135} y={230} width={52} height={65} rx={4} fill="rgba(255,255,255,.5)" stroke="#aab5c0" strokeWidth={2} />
                <rect x={138} y={233} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={162} y={233} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={138} y={264} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={162} y={264} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                {/* Windows right */}
                <rect x={313} y={230} width={52} height={65} rx={4} fill="rgba(255,255,255,.5)" stroke="#aab5c0" strokeWidth={2} />
                <rect x={316} y={233} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={340} y={233} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={316} y={264} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                <rect x={340} y={264} width={22} height={28} rx={2} fill="rgba(94,158,255,.18)" stroke="rgba(94,158,255,.3)" strokeWidth={1} />
                {/* Door */}
                <rect x={225} y={260} width={50} height={80} rx={4} fill="rgba(255,255,255,.45)" stroke="#aab5c0" strokeWidth={2} />
                {/* Ground */}
                <line x1={80} y1={340} x2={440} y2={340} stroke="rgba(122,136,150,.3)" strokeWidth={1.5} />
                {/* Heat loss arrows (hiver) */}
                <path d="M230 108 Q240 78 250 108" stroke="#ff3b30" strokeWidth={2.5} fill="none" opacity={0.8} />
                <path d="M240 98 Q250 68 260 98" stroke="#ff3b30" strokeWidth={2.5} fill="none" opacity={0.8} />
                <path d="M250 88 Q260 58 270 88" stroke="#ff3b30" strokeWidth={2.5} fill="none" opacity={0.8} />
                <text x={258} y={48} fontSize={13} fontWeight={900} fill="#b3261e" textAnchor="middle">≈31%</text>
                <text x={258} y={62} fontSize={9.5} fill="#b3261e" textAnchor="middle">Toiture</text>
                <path d="M108 240 Q88 250 108 260" stroke="#ff6a2c" strokeWidth={2} fill="none" opacity={0.7} />
                <path d="M106 260 Q86 270 106 280" stroke="#ff6a2c" strokeWidth={2} fill="none" opacity={0.7} />
                <text x={82} y={275} fontSize={12} fontWeight={800} fill="#8a4000" textAnchor="end">≈18%</text>
                <text x={82} y={288} fontSize={9} fill="#8a4000" textAnchor="end">Murs</text>
                <path d="M370 240 Q390 250 370 260" stroke="#ffd60a" strokeWidth={2} fill="none" opacity={0.65} />
                <path d="M368 260 Q388 270 368 280" stroke="#ffd60a" strokeWidth={2} fill="none" opacity={0.65} />
                <text x={395} y={275} fontSize={12} fontWeight={800} fill="#7a5d00" textAnchor="start">≈12%</text>
                <text x={395} y={288} fontSize={9} fill="#7a5d00" textAnchor="start">Fenêtres</text>
                <path d="M210 344 Q220 354 230 344" stroke="#cdd836" strokeWidth={1.8} fill="none" opacity={0.55} />
                <text x={250} y={360} fontSize={11} fontWeight={700} fill="#5a6200" textAnchor="middle">≈11% Plancher</text>
                <path d="M390 300 Q400 310 390 320" stroke="#ff9f0a" strokeWidth={1.8} fill="none" opacity={0.6} />
                <text x={415} y={330} fontSize={10} fontWeight={600} fill="#8a5b00" textAnchor="start">≈18% Ventilation</text>
                {/* Heat entry arrows (été) */}
                <path d="M420 56 Q390 70 380 100 Q360 140 260 126" stroke="#ff6a2c" strokeWidth={2.5} strokeDasharray="6,3" fill="none" opacity={0.6} />
                <text x={420} y={80} fontSize={10.5} fontWeight={700} fill="#8a4000">+ Soleil toit</text>
                <path d="M420 72 Q390 90 360 130 Q340 180 340 245" stroke="#ff9f0a" strokeWidth={2.5} strokeDasharray="6,3" fill="none" opacity={0.6} />
                <text x={345} y={200} fontSize={10} fontWeight={700} fill="#8a5b00">+ Soleil baies</text>
                {/* Legend box */}
                <rect x={18} y={14} width={145} height={52} rx={8} fill="rgba(255,255,255,.55)" stroke="rgba(255,255,255,.4)" strokeWidth={1} />
                <text x={28} y={32} fontSize={10} fontWeight={700} fill="#b3261e">➤ Flèches pleines</text>
                <text x={28} y={46} fontSize={10} fill="#41525f">Chaleur qui s&apos;échappe (hiver)</text>
                <text x={28} y={58} fontSize={10} fill="#41525f">Chaleur qui entre (été)</text>
                <rect x={18} y={16} width={4} height={40} rx={2} fill="#ff3b30" opacity={0.6} />
                <rect x={28} y={32} width={125} height={1} fill="rgba(0,0,0,.06)" />
              </svg>
              <div className="home-heat-legend">
                <div className="home-heat-legend-item"><span className="dot" style={{ background: "#ff3b30" }} /> Déperdition (hiver)</div>
                <div className="home-heat-legend-item"><span className="dot" style={{ background: "#ff6a2c", border: "2px dashed #ff6a2c" }} /> Entrée chaleur (été)</div>
              </div>
              <p style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>Répartition estimée des déperditions, exemple 90 m² classe F.</p>
            </div>

            {/* Heat-list table */}
            <div className="home-glass" style={{ padding: "20px 22px" }}>
              <table className="home-heat-table">
                <caption>Classement des postes de déperdition thermique par retour sur investissement</caption>
                <thead>
                  <tr><th scope="col">Rang</th><th scope="col">Poste</th><th scope="col">Déperdition</th><th scope="col">Retour sur investissement</th></tr>
                </thead>
                <tbody>
                  {POSTES.map((p) => (
                    <tr className="home-heat-list-item" key={p.rank}>
                      <td><span className={`rank ${p.good ? "top" : "mid"}`}>{p.rank}</span></td>
                      <th scope="row" className="poste">{p.poste}</th>
                      <td className="pct">{p.pct}</td>
                      <td className={`roi ${p.good ? "good" : "mid"}`}>{p.roi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8 }}>Délais de retour estimés sur l&apos;exemple 90 m² F.</p>
            </div>
          </div>

          <div className="home-cta-row" style={{ marginTop: 24, justifyContent: "center" }}>
            <Link to="/register" className="home-btn home-btn-primary" style={{ padding: "15px 30px", fontSize: 15 }}>Estimer mon logement</Link>
          </div>
        </section>

        {/* Disclaimer bas de page */}
        <div className="home-bottom-disclaimer">
          <div className="home-glass">
            Estimation pédagogique inspirée de la méthode 3CL-DPE 2021, sans valeur légale. Ce bilan énergétique indicatif présente des ordres de grandeur pour une maison type (90 m², classe F) ; ils dépendent de votre logement, de votre usage et des prix de l&apos;énergie. Aides estimées selon barèmes MaPrimeRénov&apos; et CEE 2026, sous conditions d&apos;éligibilité. Pour un DPE officiel opposable, faites appel à un diagnostiqueur certifié (annuaire public).
          </div>
        </div>
      </div>
    </Layout>
  );
}
